import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserDetails } from "../../store/slices/authSlice";
import { ChevronDown } from "lucide-react";
import "./styles/dashboard.css";
import SummaryCards from "./SummaryCards";
import SalesOverview from "./SalesOverview";
import QuickActions from "./QuickActions";
import TopCustomers from "./TopCustomers";
import UserPerformance from "./UserPerformance";
import SalesSummary from "./SalesSummary";
import LowStockAlerts from "./LowStockAlerts";

const Dashboard = () => {
  const [dateRange, setDateRange] = useState("This Month");
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // Custom date picker states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);

  useEffect(() => {
    if (!user?.name) {
      dispatch(fetchUserDetails());
    }
  }, [dispatch, user]);

  const ranges = [
    "Today",
    "Yesterday",
    "Last 7 Days",
    "This Month",
    "Custom Date",
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsRangeOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Date picker helper functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return date > today;
  };

  const isDateInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  const isDateSelected = (date) => {
    if (!selectedStartDate && !selectedEndDate) return false;
    return (
      (selectedStartDate && date.getTime() === selectedStartDate.getTime()) ||
      (selectedEndDate && date.getTime() === selectedEndDate.getTime())
    );
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      setSelectedStartDate(date);
      setSelectedEndDate(null);
    } else if (selectedStartDate && !selectedEndDate) {
      if (date < selectedStartDate) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(date);
      } else {
        setSelectedEndDate(date);
      }
      // Close date picker and update date range after selecting end date
      setTimeout(() => {
        setShowDatePicker(false);
        setDateRange("Custom Date");
        setRefreshKey((prev) => prev + 1);
      }, 300);
    }
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = (firstDay.getDay() + 6) % 7;

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDateRange = () => {
    if (selectedStartDate && selectedEndDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      const endStr = selectedEndDate.toLocaleDateString("en-GB");
      return `${startStr} - ${endStr}`;
    } else if (selectedStartDate) {
      const startStr = selectedStartDate.toLocaleDateString("en-GB");
      return `${startStr} - `;
    }
    return dateRange;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content-card">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-welcome-text">
              Welcome Back, {user?.name || "User"}!
            </h1>
            <p className="dashboard-subtitle">
              Here's an overview of your business performance — track customers,
              orders, and finances in real time.
            </p>
          </div>

          <div
            className="dashboard-date-filter"
            ref={dropdownRef}
            style={{ position: "relative", gap: "10px" }}
          >
            {/* Date Filter Dropdown */}
            <button
              className="dashboard-btn-filter"
              onClick={() => setIsRangeOpen((o) => !o)}
              style={{
                minWidth:
                  dateRange === "Custom Date" &&
                  selectedStartDate &&
                  selectedEndDate
                    ? "220px"
                    : "170px",
                justifyContent: "space-between",
                height: "42px",
                padding: "10px 12px",
                borderRadius: "10px",
                fontSize: "14px",
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                {dateRange === "Custom Date" &&
                selectedStartDate &&
                selectedEndDate
                  ? formatDateRange()
                  : dateRange}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {isRangeOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "48px",
                  right: 0,
                  background: "#fff",
                  border: "1px solid #E5E7EB",
                  boxShadow: "0px 10px 30px rgba(0,0,0,0.08)",
                  borderRadius: "12px",
                  padding: "10px",
                  minWidth: "13.5rem",
                  zIndex: 10,
                }}
              >
                {ranges.map((range) => (
                  <button
                    key={range}
                    onClick={() => {
                      if (range === "Custom Date") {
                        setIsRangeOpen(false);
                        setShowDatePicker(true);
                      } else {
                        setDateRange(range);
                        setSelectedStartDate(null);
                        setSelectedEndDate(null);
                        setRefreshKey((prev) => prev + 1);
                        setIsRangeOpen(false);
                      }
                    }}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      border: "none",
                      background:
                        dateRange === range ? "#F3F4F6" : "transparent",
                      borderRadius: "8px",
                      color: "#111827",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <span>{range}</span>
                    {dateRange === range && (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#2563EB"
                        strokeWidth="2"
                      >
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Refresh Button */}
            <button
              className="dashboard-btn-filter"
              onClick={() => setRefreshKey((prev) => prev + 1)}
              style={{ padding: "0", width: "38px", justifyContent: "center" }}
            >
              <svg
                width="17"
                height="17"
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.5 1.20861V5.33361C16.5 5.51595 16.4276 5.69082 16.2986 5.81975C16.1697 5.94868 15.9948 6.02111 15.8125 6.02111H11.6875C11.5514 6.02122 11.4184 5.98096 11.3053 5.90542C11.1921 5.82988 11.1039 5.72247 11.0518 5.59678C10.9997 5.47109 10.9861 5.33277 11.0127 5.19934C11.0393 5.06591 11.1048 4.94336 11.2011 4.84721L12.7746 3.27111C11.5088 2.05831 9.82514 1.37863 8.07211 1.37275H8.03344C6.23713 1.36923 4.51166 2.07313 3.23039 3.33213C3.0991 3.45469 2.92505 3.52101 2.74548 3.51689C2.56592 3.51277 2.3951 3.43855 2.26956 3.31009C2.14402 3.18163 2.07374 3.00915 2.07375 2.82954C2.07376 2.64992 2.14406 2.47745 2.26961 2.349C3.8022 0.85161 5.85724 0.00921741 7.99989 7.51938e-05C10.1425 -0.00906703 12.2047 0.815758 13.75 2.30002L15.3278 0.722207C15.4241 0.626537 15.5465 0.561489 15.6796 0.535255C15.8127 0.509021 15.9507 0.522775 16.076 0.574785C16.2013 0.626795 16.3085 0.714734 16.3839 0.827525C16.4594 0.940315 16.4998 1.07291 16.5 1.20861ZM13.2696 12.8351C11.9961 14.0789 10.2899 14.7803 8.50981 14.7917C6.72975 14.803 5.01466 14.1235 3.72539 12.8961L5.29891 11.3226C5.39606 11.2266 5.4624 11.1038 5.48949 10.9699C5.51658 10.8361 5.50317 10.6972 5.45098 10.5709C5.3988 10.4447 5.3102 10.3369 5.19648 10.2612C5.08276 10.1856 4.94909 10.1455 4.8125 10.1461H0.6875C0.505164 10.1461 0.330296 10.2185 0.201364 10.3475C0.0724332 10.4764 2.12988e-07 10.6513 2.12988e-07 10.8336V14.9586C-0.000106856 15.0947 0.0401563 15.2277 0.115692 15.3409C0.191229 15.454 0.298641 15.5422 0.424332 15.5943C0.550023 15.6464 0.68834 15.66 0.821774 15.6334C0.955207 15.6068 1.07776 15.5413 1.17391 15.445L2.75 13.8672C4.27298 15.337 6.30531 16.1611 8.42188 16.1669H8.46742C10.6228 16.1724 12.6936 15.3286 14.2313 13.8182C14.3568 13.6898 14.4271 13.5173 14.4271 13.3377C14.4271 13.1581 14.3568 12.9856 14.2313 12.8571C14.1058 12.7287 13.9349 12.6545 13.7554 12.6503C13.5758 12.6462 13.4018 12.7125 13.2705 12.8351H13.2696Z"
                  fill="#2F80ED"
                />
              </svg>
            </button>
          </div>

          {/* Custom Date Picker Popup */}
          {showDatePicker && (
            <>
              <div
                className="dashboard-date-picker-backdrop"
                onClick={() => setShowDatePicker(false)}
              ></div>
              <div className="dashboard-date-picker" ref={datePickerRef}>
                <div className="dashboard-date-picker-header">
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(90deg)" }}
                    />
                  </button>
                  <h3 className="dashboard-date-picker-month-year">
                    {currentMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </h3>
                  <button
                    className="dashboard-date-picker-nav-btn"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronDown
                      size={16}
                      style={{ transform: "rotate(-90deg)" }}
                    />
                  </button>
                </div>

                <div className="dashboard-date-picker-weekdays">
                  <div className="dashboard-weekday">Mo</div>
                  <div className="dashboard-weekday">Tu</div>
                  <div className="dashboard-weekday">We</div>
                  <div className="dashboard-weekday">Th</div>
                  <div className="dashboard-weekday">Fr</div>
                  <div className="dashboard-weekday">Sa</div>
                  <div className="dashboard-weekday">Su</div>
                </div>

                <div className="dashboard-date-picker-days">
                  {getDaysInMonth(currentMonth).map((day, index) => (
                    <div
                      key={index}
                      className={`dashboard-date-picker-day ${
                        day ? "" : "empty"
                      } ${day && isDateDisabled(day) ? "disabled" : ""} ${
                        day && isDateInRange(day) ? "in-range" : ""
                      } ${day && isDateSelected(day) ? "selected" : ""}`}
                      onClick={() => day && handleDateClick(day)}
                    >
                      {day ? day.getDate() : ""}
                    </div>
                  ))}
                </div>

                {selectedStartDate && selectedEndDate && (
                  <div className="dashboard-date-picker-footer">
                    <span className="dashboard-date-picker-selected">
                      {formatDateRange()}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Summary Cards */}
        <SummaryCards
          dateRange={dateRange}
          refreshKey={refreshKey}
          customStartDate={selectedStartDate}
          customEndDate={selectedEndDate}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            marginTop: "24px",
          }}
        >
          {/* Row 1: Sales Overview & Quick Actions */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr",
              gap: "24px",
            }}
          >
            <SalesOverview />
            <QuickActions />
          </div>

          {/* Row 2: Top Customers */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
            }}
          >
            <TopCustomers type="purchase" />
            <TopCustomers type="credit" />
          </div>

          {/* Row 3: User Performance & Sales Summary Stack */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
            }}
          >
            <UserPerformance />
            <div
              style={{ display: "flex", flexDirection: "column", gap: "24px" }}
            >
              <SalesSummary />
              <LowStockAlerts />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
